import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Calculator, Loader2, Search, Undo2, UserRoundPlus, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/features/auth/auth.store';
import { useCustomerSearch } from '@/features/customers/customers.api';
import { formatCurrency, formatDateTime, initials } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  PAGE_SIZE,
  useAttachOwner,
  useCardHistory,
  useGiftCardByCode,
  useRecalculateBalance,
  useRemoveOwner,
  useRevertTransaction,
  useSetNickname,
} from './gift-cards.api';
import { TRANSACTION_TYPE, ownerName, type CardTransaction } from './types';

/**
 * One card: its balance, who holds it, and everything that has moved on it.
 *
 * Replaces `GiftCardsHistoryModal` (745 lines), which was mounted three times —
 * once per legacy tab — each copy with its own state.
 *
 * The header is refetched rather than passed in, so a nickname change or a
 * reverted transaction is reflected without closing the drawer. It is keyed by
 * **code**: `giftcards/balance/{giftCardId}` matches on `Code` despite its
 * route parameter's name, while the ledger and every write below decode an id.
 * Both are carried for that reason.
 */

export interface CardRef {
  id: string;
  code: string | null;
}

function Section({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold tracking-wide uppercase">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export function GiftCardDetailSheet({
  card,
  open,
  onOpenChange,
}: {
  card: CardRef | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const canReverse = useAuthStore((s) => s.can('canReverseGiftCards'));

  const [page, setPage] = useState(0);
  const [nickname, setNickname] = useState('');
  const [ownerDraft, setOwnerDraft] = useState('');
  const [ownerTerm, setOwnerTerm] = useState('');
  const [pickingOwner, setPickingOwner] = useState(false);
  const [reverting, setReverting] = useState<CardTransaction | null>(null);

  const detail = useGiftCardByCode(open ? (card?.code ?? null) : null);
  const history = useCardHistory(open ? (card?.id ?? null) : null, page);
  const userSearch = useCustomerSearch(ownerTerm);

  const setNicknameMutation = useSetNickname();
  const attachOwner = useAttachOwner();
  const removeOwner = useRemoveOwner();
  const recalculate = useRecalculateBalance();
  const revert = useRevertTransaction();

  // A different card is a different drawer; nothing carries over.
  useEffect(() => {
    setPage(0);
    setOwnerDraft('');
    setOwnerTerm('');
    setPickingOwner(false);
  }, [card?.id]);

  // Seeded from the server rather than from the row that opened the drawer, so
  // it is right even when the list behind it is stale.
  useEffect(() => {
    setNickname(detail.data?.nickname ?? '');
  }, [detail.data?.nickname, detail.data?.id]);

  const info = detail.data;
  const rows = history.data?.transactions ?? [];
  const total = history.data?.total ?? 0;
  const busy =
    setNicknameMutation.isPending ||
    attachOwner.isPending ||
    removeOwner.isPending ||
    recalculate.isPending;

  const failed = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : t('giftCards.actionFailed')
    );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle className="flex flex-wrap items-center gap-2">
            <span className="group flex items-center gap-1 font-mono text-sm">
              {card?.code ?? '—'}
              {card?.code && <CopyButton value={card.code} />}
            </span>
            {info &&
              (info.activatedOn ? (
                <Badge variant="success">{t('giftCards.activatedBadge')}</Badge>
              ) : (
                <Badge variant="secondary">
                  {t('giftCards.notActivated')}
                </Badge>
              ))}
          </SheetTitle>
          {detail.isPending ? (
            <Skeleton className="h-7 w-32" />
          ) : (
            // Refetched after a recalculation or a reversal.
            <div className="relative w-fit rounded-md">
              <BusyOverlay show={detail.isFetching} size="sm" />
              <p
                className={cn(
                  'text-2xl font-semibold tabular-nums',
                  detail.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                {formatCurrency(info?.currentBalanceLeft ?? 0)}
              </p>
            </div>
          )}
        </SheetHeader>

        {detail.isError && (
          <p className="text-destructive p-4 text-sm">
            {t('giftCards.cardLoadFailed')}
          </p>
        )}

        {detail.isPending && (
          <div className="space-y-6 p-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-9 w-full" />
              </div>
            ))}
          </div>
        )}

        {info && (
          <div className="space-y-6 p-4">
            {/* ------------------------------------------------- nickname */}
            <Section title={t('giftCards.nickname')}>
              <div className="flex gap-2">
                <Input
                  value={nickname}
                  disabled={busy}
                  placeholder={t('giftCards.nicknamePlaceholder')}
                  onChange={(event) => setNickname(event.target.value)}
                  aria-label={t('giftCards.nickname')}
                />
                <Button
                  variant="outline"
                  disabled={busy || nickname === (info.nickname ?? '')}
                  onClick={() =>
                    setNicknameMutation.mutate(
                      { id: info.id, nickname },
                      {
                        onSuccess: () => {
                          toast.success(t('giftCards.nicknameSaved'));
                          void detail.refetch();
                        },
                        onError: failed,
                      }
                    )
                  }
                >
                  {setNicknameMutation.isPending && (
                    <Loader2 className="animate-spin" />
                  )}
                  {t('common.save')}
                </Button>
              </div>
            </Section>

            {/* ---------------------------------------------------- owner */}
            <Section
              title={t('giftCards.owner')}
              action={
                info.owner ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      removeOwner.mutate(info.id, {
                        onSuccess: () => {
                          toast.success(t('giftCards.ownerRemoved'));
                          void detail.refetch();
                        },
                        onError: failed,
                      })
                    }
                  >
                    <X className="size-3.5" />
                    {t('giftCards.removeOwner')}
                  </Button>
                ) : (
                  !pickingOwner && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => setPickingOwner(true)}
                    >
                      <UserRoundPlus className="size-3.5" />
                      {t('giftCards.assignOwner')}
                    </Button>
                  )
                )
              }
            >
              {info.owner ? (
                <div className="flex items-center gap-2.5 rounded-xl border p-2.5">
                  <Avatar className="size-9 shrink-0">
                    {info.owner.avatar && (
                      <AvatarImage src={info.owner.avatar} alt="" />
                    )}
                    <AvatarFallback>
                      {initials(info.owner.firstName, info.owner.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="truncate text-sm">{ownerName(info.owner)}</p>
                    <p className="text-muted-foreground truncate text-xs">
                      {info.owner.email}
                    </p>
                  </div>
                </div>
              ) : pickingOwner ? (
                <div className="space-y-2">
                  <form
                    role="search"
                    className="flex gap-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      setOwnerTerm(ownerDraft.trim());
                    }}
                  >
                    <div className="relative flex-1">
                      <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                      <Input
                        value={ownerDraft}
                        onChange={(event) => setOwnerDraft(event.target.value)}
                        placeholder={t('giftCards.searchUser')}
                        aria-label={t('giftCards.searchUser')}
                        className="pl-9"
                      />
                    </div>
                    <Button type="submit" disabled={userSearch.isFetching}>
                      {userSearch.isFetching ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        t('common.search')
                      )}
                    </Button>
                  </form>

                  {userSearch.isSuccess &&
                    (userSearch.data?.results ?? []).length === 0 && (
                      <p className="text-muted-foreground text-xs">
                        {t('giftCards.noUsersFound')}
                      </p>
                    )}

                  {(userSearch.data?.results ?? []).length > 0 && (
                    <div className="max-h-56 divide-y overflow-y-auto rounded-xl border">
                      {userSearch.data!.results.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          className="hover:bg-accent flex w-full items-center gap-2.5 p-2.5 text-left transition-colors"
                          onClick={() =>
                            attachOwner.mutate(
                              { id: info.id, userId: user.id },
                              {
                                onSuccess: () => {
                                  toast.success(t('giftCards.ownerAssigned'));
                                  setPickingOwner(false);
                                  setOwnerDraft('');
                                  setOwnerTerm('');
                                  void detail.refetch();
                                },
                                onError: failed,
                              }
                            )
                          }
                        >
                          <Avatar className="size-8 shrink-0">
                            {user.avatar && (
                              <AvatarImage src={user.avatar} alt="" />
                            )}
                            <AvatarFallback>
                              {initials(user.firstName, user.lastName)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="truncate text-sm">
                              {[user.firstName, user.lastName]
                                .filter(Boolean)
                                .join(' ') || '—'}
                            </p>
                            <p className="text-muted-foreground truncate text-xs">
                              {user.email}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  {t('giftCards.unassigned')}
                </p>
              )}
            </Section>

            {/* -------------------------------------------------- ledger */}
            <Section
              title={t('giftCards.history')}
              action={
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    recalculate.mutate(info.id, {
                      onSuccess: () => {
                        toast.success(t('giftCards.balanceRecalculated'));
                        void detail.refetch();
                      },
                      onError: failed,
                    })
                  }
                >
                  {recalculate.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Calculator className="size-3.5" />
                  )}
                  {t('giftCards.recalculate')}
                </Button>
              }
            >
              {history.isPending && (
                <div className="divide-y rounded-xl border">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="space-y-1.5 p-3">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  ))}
                </div>
              )}

              {history.isSuccess && rows.length === 0 && (
                <p className="text-muted-foreground rounded-xl border border-dashed py-8 text-center text-sm">
                  {t('giftCards.noCardHistory')}
                </p>
              )}

              {rows.length > 0 && (
                <div className="space-y-2">
                  <div className="relative rounded-xl">
                    <BusyOverlay show={history.isFetching} />
                    <div
                      className={cn(
                        'divide-y rounded-xl border',
                        history.isFetching && 'opacity-60 transition-opacity'
                      )}
                    >
                      {rows.map((row) => {
                        const isLoad =
                          row.transactionType === TRANSACTION_TYPE.load;
                        return (
                          <div
                            key={row.id}
                            className="flex items-start gap-3 p-3 text-sm"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="font-medium">
                                {isLoad
                                  ? t('giftCards.load')
                                  : t('giftCards.redemption')}
                              </p>
                              <p className="text-muted-foreground text-xs">
                                {formatDateTime(row.createdAt)}
                                {row.orderBusinessName &&
                                  ` · ${row.orderBusinessName}`}
                              </p>
                              {row.orderConfirmationCode && (
                                <p className="text-muted-foreground font-mono text-xs">
                                  {row.orderConfirmationCode}
                                </p>
                              )}
                            </div>
                            <div className="shrink-0 text-right">
                              <p
                                className={
                                  isLoad
                                    ? 'text-success tabular-nums'
                                    : 'tabular-nums'
                                }
                              >
                                {isLoad ? '+' : '−'}
                                {formatCurrency(Math.abs(row.amount)).replace(
                                  /^-/,
                                  ''
                                )}
                              </p>
                              <p className="text-muted-foreground text-xs tabular-nums">
                                {formatCurrency(row.giftCardCurrentBalance)}
                              </p>
                            </div>
                            {canReverse && (
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={t('giftCards.revert')}
                                onClick={() => setReverting(row)}
                              >
                                <Undo2 className="size-4" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <Pagination
                    page={page}
                    size={PAGE_SIZE}
                    total={total}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </Section>
          </div>
        )}

        <AlertDialog
          open={reverting !== null}
          onOpenChange={(next) => {
            if (!next) setReverting(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('giftCards.revert')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('giftCards.revertConfirm', {
                  amount: formatCurrency(Math.abs(reverting?.amount ?? 0)),
                  code: card?.code ?? '',
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() =>
                  revert.mutate(reverting!.id, {
                    onSuccess: () => {
                      toast.success(t('giftCards.reverted'));
                      setReverting(null);
                      void detail.refetch();
                    },
                    onError: failed,
                  })
                }
              >
                {t('giftCards.revert')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
